import React, { useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchComparativo,
  fetchComparativoTendencia,
  fetchComparativoHeatmap,
  setComparativoFilters,
  clearComparativoFilters,
} from "../../../slices/biSlice";
import usePolling from "../../../hooks/usePolling";
import { formatColones } from "../../../utils/biCalculations";
import KPICard from "../../../components/BI/KPICard";
import DataTable from "../../../components/BI/DataTable";
import ExportButtons from "../../../components/BI/ExportButtons";
import LineChartBI from "../../../components/BI/LineChartBI";
import HeatmapBI from "../../../components/BI/HeatmapBI";
import PollingIndicator from "../../../components/BI/PollingIndicator";

const baseUrl = process.env.REACT_APP_API_URL;

/**
 * Opciones de vista para el selector de comparación.
 */
const VISTA_OPTIONS = [
  { value: "mesAnterior", label: "Mes anterior" },
  { value: "mismoMesAnoAnterior", label: "Mismo mes año anterior" },
  { value: "ytd", label: "YTD" },
];

/**
 * Opciones de segmentación para el selector.
 */
const SEGMENTO_OPTIONS = [
  { value: "tienda", label: "Tienda" },
  { value: "producto", label: "Producto" },
  { value: "marca", label: "Marca" },
  { value: "categoria", label: "Categoría" },
];

/**
 * Calcula la variación porcentual entre dos valores.
 * Retorna "N/A" si alguno de los períodos es 0.
 */
function calcularVariacion(actual, anterior) {
  if (actual === 0 && anterior === 0) return "N/A";
  if (anterior === 0) return "N/A";
  return ((actual - anterior) / anterior) * 100;
}

/**
 * Formatea la variación como texto con signo.
 */
function formatVariacion(variacion) {
  if (variacion === "N/A" || variacion == null) return "N/A";
  const num = Number(variacion);
  if (isNaN(num)) return "N/A";
  const prefix = num > 0 ? "+" : "";
  return `${prefix}${num.toFixed(1)}%`;
}

/**
 * Determina la tendencia (trend) para el color.
 */
function getVariacionTrend(variacion) {
  if (variacion === "N/A" || variacion == null) return "neutral";
  const num = Number(variacion);
  if (isNaN(num)) return "neutral";
  if (num > 0) return "up";
  if (num < 0) return "down";
  return "neutral";
}

/**
 * Formatea variación absoluta en colones.
 */
function formatVariacionAbsoluta(actual, anterior) {
  if (actual == null || anterior == null) return "₡0";
  const diff = actual - anterior;
  const prefix = diff > 0 ? "+" : "";
  return `${prefix}${formatColones(diff).replace("₡", "₡")}`;
}

/**
 * Columnas de la tabla comparativa principal.
 */
const comparativoColumns = [
  { key: "nombreSegmento", label: "Segmento", sortable: true },
  {
    key: "ventasActual",
    label: "Ventas Actual",
    sortable: true,
    render: (row) => formatColones(row.ventasActual),
  },
  {
    key: "ventasAnterior",
    label: "Ventas Anterior",
    sortable: true,
    render: (row) => formatColones(row.ventasAnterior),
  },
  {
    key: "variacionAbsoluta",
    label: "Variación (₡)",
    sortable: true,
    render: (row) => {
      if (row.ventasActual === 0 && row.ventasAnterior === 0) return "N/A";
      if (row.ventasAnterior === 0) return "N/A";
      const diff = row.ventasActual - row.ventasAnterior;
      const color = diff > 0 ? "text-green-600" : diff < 0 ? "text-red-600" : "text-gray-600";
      const prefix = diff > 0 ? "+" : "";
      return <span className={color}>{prefix}{formatColones(diff)}</span>;
    },
  },
  {
    key: "variacionPorcentual",
    label: "Variación %",
    sortable: true,
    render: (row) => {
      const variacion = calcularVariacion(row.ventasActual, row.ventasAnterior);
      const trend = getVariacionTrend(variacion);
      const color =
        trend === "up" ? "text-green-600" : trend === "down" ? "text-red-600" : "text-gray-500";
      return <span className={color}>{formatVariacion(variacion)}</span>;
    },
  },
];

/**
 * Columnas para tablas de ranking (Top 20 crecimiento / caída).
 */
const rankingColumns = [
  { key: "nombreProducto", label: "Producto", sortable: false },
  { key: "marca", label: "Marca" },
  {
    key: "ventasActual",
    label: "Ventas Actual",
    render: (row) => formatColones(row.ventasActual),
  },
  {
    key: "ventasAnterior",
    label: "Ventas Anterior",
    render: (row) => formatColones(row.ventasAnterior),
  },
  {
    key: "variacionPorcentual",
    label: "Variación %",
    render: (row) => {
      const variacion = row.variacionPorcentual;
      const trend = getVariacionTrend(variacion);
      const color =
        trend === "up" ? "text-green-600" : trend === "down" ? "text-red-600" : "text-gray-500";
      return <span className={color}>{formatVariacion(variacion)}</span>;
    },
  },
];

export function ComparativoPage() {
  const dispatch = useDispatch();
  const {
    metricas,
    tablaComparativa,
    top20Crecimiento,
    top20Caida,
    tendencia,
    heatmap,
    loading,
    loadingTendencia,
    loadingHeatmap,
    error,
    filters,
  } = useSelector((state) => state.bi.comparativo);
  const token = useSelector((state) => state.app.user?.token);

  // Polling: fetch principal cada 15 min
  const fetchData = useCallback(() => {
    return dispatch(fetchComparativo());
  }, [dispatch]);

  const { lastUpdated, isStale, refetch } = usePolling(fetchData);

  // Cargar tendencia y heatmap al montar
  useEffect(() => {
    dispatch(fetchComparativoTendencia());
    dispatch(fetchComparativoHeatmap());
  }, [dispatch]);

  // Re-fetch cuando cambian filtros
  useEffect(() => {
    dispatch(fetchComparativo());
  }, [dispatch, filters]);

  // Handler de cambio de vista
  const handleVistaChange = (vista) => {
    dispatch(setComparativoFilters({ vista }));
  };

  // Handler de cambio de segmento
  const handleSegmentoChange = (segmento) => {
    dispatch(setComparativoFilters({ segmento }));
  };

  // Handler de limpiar filtros
  const handleClearFilters = () => {
    dispatch(clearComparativoFilters());
  };

  // URL de exportación con filtros actuales
  const exportUrl = useMemo(() => {
    const params = Object.entries(filters)
      .filter(([, v]) => v !== null && v !== undefined && v !== "")
      .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
      .join("&");
    const tokenParam = token ? `${params ? "&" : ""}access_token=${token}` : "";
    return `${baseUrl}/bi/comparativo/export/excel${params || tokenParam ? `?${params}${tokenParam}` : ""}`;
  }, [filters, token]);

  // Calcular variación de métricas principales
  const ventasActual = metricas?.ventasActual ?? 0;
  const ventasAnterior = metricas?.ventasAnterior ?? 0;
  const variacionMetricas = metricas?.variacion ?? calcularVariacion(ventasActual, ventasAnterior);
  const variacionTrend = getVariacionTrend(variacionMetricas);

  // Extraer tiendas del heatmap para el componente HeatmapBI
  const heatmapTiendas = React.useMemo(() => {
    const tiendasSet = new Set();
    (heatmap || []).forEach((d) => {
      if (d.tienda) tiendasSet.add(d.tienda);
    });
    return Array.from(tiendasSet);
  }, [heatmap]);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-gray-900">
          Dashboard Comparativo
        </h1>
        <div className="flex items-center gap-3">
          <PollingIndicator
            lastUpdated={lastUpdated}
            isStale={isStale}
            onRefresh={refetch}
          />
          <ExportButtons
            exportUrl={exportUrl}
            disabled={loading || (!tablaComparativa.length && !top20Crecimiento.length)}
          />
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Selector de Vista */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-600 mb-1">
              Comparar con
            </label>
            <div className="flex gap-2">
              {VISTA_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => handleVistaChange(opt.value)}
                  className={`px-4 py-2 text-sm rounded-md border transition-colors ${
                    filters.vista === opt.value
                      ? "bg-purple text-white border-purple"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-600 mb-1">
              Segmentar por
            </label>
            <div className="flex gap-2">
              {SEGMENTO_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => handleSegmentoChange(opt.value)}
                  className={`px-4 py-2 text-sm rounded-md border transition-colors ${
                    filters.segmento === opt.value
                      ? "bg-purple text-white border-purple"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <button
            type="button"
            onClick={handleClearFilters}
            className="text-sm text-gray-500 hover:text-gray-700 underline self-end py-2"
          >
            Limpiar filtros
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <KPICard
          title="Ventas Período Actual"
          value={formatColones(ventasActual)}
          loading={loading && !metricas}
        />
        <KPICard
          title="Ventas Período Anterior"
          value={formatColones(ventasAnterior)}
          loading={loading && !metricas}
        />
        <KPICard
          title="Variación"
          value={formatVariacion(variacionMetricas)}
          trend={variacionTrend}
          variation={
            variacionTrend !== "neutral"
              ? `${formatVariacionAbsoluta(ventasActual, ventasAnterior)}`
              : undefined
          }
          loading={loading && !metricas}
        />
      </div>

      {/* Tabla Comparativa */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-3">
          Comparativo por Segmento
        </h2>
        <DataTable
          columns={comparativoColumns}
          data={tablaComparativa}
          loading={loading}
          emptyMessage="No hay datos comparativos para los filtros seleccionados."
        />
      </div>

      {/* Rankings: Top 20 Crecimiento y Top 20 Caída */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Top 20 Crecimiento */}
        <div>
          <h2 className="text-lg font-semibold text-gray-800 mb-3">
            Top 20 Mayor Crecimiento
          </h2>
          <DataTable
            columns={rankingColumns}
            data={top20Crecimiento}
            loading={loading}
            emptyMessage="Sin datos de crecimiento disponibles."
          />
        </div>

        {/* Top 20 Caída */}
        <div>
          <h2 className="text-lg font-semibold text-gray-800 mb-3">
            Top 20 Mayor Caída
          </h2>
          <DataTable
            columns={rankingColumns}
            data={top20Caida}
            loading={loading}
            emptyMessage="Sin datos de caída disponibles."
          />
        </div>
      </div>

      {/* LineChart: Tendencia 24 meses */}
      <div className="mb-6">
        {loadingTendencia ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
              <div className="h-64 bg-gray-100 rounded"></div>
            </div>
          </div>
        ) : (
          <LineChartBI
            data={tendencia}
            title="Tendencia de Ventas — Últimos 24 Meses"
            height={320}
          />
        )}
      </div>

      {/* HeatmapBI: Meses vs Tiendas */}
      <div className="mb-6">
        {loadingHeatmap ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
              <div className="h-48 bg-gray-100 rounded"></div>
            </div>
          </div>
        ) : (
          <HeatmapBI
            data={heatmap}
            tiendas={heatmapTiendas}
            title="Heatmap de Ventas — Meses vs Tiendas"
          />
        )}
      </div>
    </div>
  );
}

export default ComparativoPage;
